/**
 * MedListReview — on-screen review of a built medication list before it is
 * saved or downloaded (med_list tool). Shared by the tool page
 * (MedicationList) and the record-card dialog (MedListDialog).
 *
 * The medicines are edited as rows, one card per medicine
 * (MedListItemsEditor, the record form's words and When ticks). Under them,
 * the five company-template sections (Morning / Afternoon / Evening /
 * Bedtime / As needed) render read-only as they will print, built from the
 * rows by rowsToBuckets, so the CM checks every medication, dose, and block
 * before downloading: that review IS the safety gate for a tool that
 * touches medication data. The header fields are editable inline:
 *
 *   - Client: free text, or the name as text when the list is LINKED to a
 *     client record (the record is the source; the birth date shows under
 *     it). Typing a name drops the record's structured name: free text is
 *     never split, so the file is then named for the text as written.
 *   - Date of the list: the house date box, pre-filled with the practice's
 *     today and capped there, its limit said up front. A future day shows
 *     the copy and blocks Save / Download.
 *   - Allergies: free text when unlinked. When linked, the record's
 *     NKDA-aware allergies line renders READ-ONLY in the same red register
 *     the record's critical strip uses, with a "Change allergies on the
 *     record" door; a medication list must never carry allergies that
 *     disagree with the record.
 *
 * A blank cell reads "Not given" on screen; the document keeps its blank.
 * The six printed columns are the document's (Medication, Prescribed by,
 * Dose, Method, Purpose, Notes or timing); the table scrolls sideways
 * inside its frame on a narrow screen rather than clipping.
 *
 * Nothing here is ever written back to the record; this is a formatted
 * document, not a record edit.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  MED_LIST_BUCKETS,
  MED_LIST_BUCKET_LABELS,
  isMedListDateValid,
  itemsOf,
  medListChangeWords,
  totalMedCount,
  withItems,
  type MedListDoc,
} from '@/lib/medList';
import { MedListItemsEditor, type MedListCareTeam } from '@/components/aiTools/MedListItemsEditor';
import { proseDate } from '@/lib/proseDate';
import { Field } from '@/components/primitives/Field';
import { displayValue } from '@/lib/valueCase';
import { DateInput } from '@/components/primitives/DateInput';

const FIELD_CLS =
  'mt-1 block w-full min-h-10 rounded-[10px] border border-sage-400/20 bg-paper px-3 py-2 text-sm text-ink placeholder:text-sage-300 outline-none focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-[12px] font-semibold text-sage-800';

// The printed columns in their order (api/_shared/aiTools/medListColumns.ts);
// the prescriber sits after the medication name (LP-32).
const COLUMNS: { key: keyof MedRowView; label: string }[] = [
  { key: 'medication', label: 'Medication' },
  { key: 'prescriber', label: 'Prescribed by' },
  { key: 'dose', label: 'Dose' },
  { key: 'method', label: 'Method' },
  { key: 'purpose', label: 'Purpose' },
  { key: 'notes', label: 'Notes or timing' },
];

type MedRowView = MedListDoc['buckets']['morning'][number];

/** What a linked client record contributes to the header (read-only). */
export interface MedListLinkedClient {
  clientId: string;
  displayName: string;
  /** YYYY-MM-DD or null when not recorded. */
  dob: string | null;
  /** The record page, for the "Change allergies on the record" door. */
  recordHref: string;
}

export interface MedListReviewProps {
  doc: MedListDoc;
  onChange: (doc: MedListDoc) => void;
  /** Empty-state copy when there are no meds (differs by input path). */
  emptyLabel: string;
  /** The practice's today (YYYY-MM-DD): the date's default and ceiling. */
  today: string;
  /** Present when the list is linked to a client record: name + allergies
   *  come from the record and render read-only. */
  linkedClient?: MedListLinkedClient | null;
  /** The linked client's care team, for the Prescriber picker. */
  careTeam?: MedListCareTeam | null;
}

export function MedListReview({
  doc,
  onChange,
  emptyLabel,
  today,
  linkedClient = null,
  careTeam = null,
}: MedListReviewProps): ReactNode {
  const items = itemsOf(doc);
  const total = totalMedCount(doc.buckets);
  const changes = doc.changes_coming ?? [];
  const dateValid = isMedListDateValid(doc.date, today);

  const setField = (
    patch: Partial<Pick<MedListDoc, 'client_name' | 'client_last_name' | 'client_first_name' | 'date' | 'allergies'>>,
  ): void => {
    onChange({ ...doc, ...patch });
  };

  return (
    <div className="space-y-4" data-testid="med-list-review">
      {/* Header block: Client / Date / Allergies. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          {linkedClient ? (
            <>
              <span className={LABEL_CLS}>Client</span>
              <p className="mt-1 text-sm font-semibold text-ink" data-testid="med-list-client">
                {doc.client_name ?? ''}
              </p>
              <p className="mt-1 text-xs text-ink-soft" data-testid="med-list-review-dob">
                {linkedClient.dob
                  ? `Born ${proseDate(linkedClient.dob)}`
                  : 'Date of birth not recorded'}
              </p>
            </>
          ) : (
            <>
              <label htmlFor="ml-client" className={LABEL_CLS}>
                Client
              </label>
              <input
                id="ml-client"
                type="text"
                className={FIELD_CLS}
                placeholder="[Client Name]"
                value={doc.client_name ?? ''}
                onChange={(e) =>
                  setField({
                    client_name: e.target.value.length > 0 ? e.target.value : null,
                    client_last_name: null,
                    client_first_name: null,
                  })
                }
                data-testid="med-list-client"
              />
            </>
          )}
        </div>
        <Field
          label="Date of the list"
          htmlFor="ml-date"
          help="No later than today."
          error={
            dateValid
              ? undefined
              : doc.date === null || doc.date === ''
                ? 'Pick the date of this list.'
                : 'The date cannot be after today.'
          }
          errorTestId="med-list-date-error"
        >
          <DateInput
            id="ml-date"
            size="field" className="w-full max-w-[11rem]"
            value={doc.date ?? ''}
            max={today}
            onChange={(e) => setField({ date: e.target.value.length > 0 ? e.target.value : null })}
            aria-invalid={!dateValid}
            aria-describedby={dateValid ? 'ml-date-help' : 'ml-date-help ml-date-error'}
            data-testid="med-list-date"
          />
        </Field>
        <div>
          <span className={LABEL_CLS} id="ml-allergies-label">
            Allergies
          </span>
          {linkedClient ? (
            <div
              className="mt-1 rounded-[10px] border border-pepper/30 bg-pepper-soft px-3 py-2 text-sm font-medium text-pepper-deep"
              role="note"
              aria-labelledby="ml-allergies-label"
              data-testid="med-list-allergies-linked"
            >
              <span>{displayValue(doc.allergies, 'allergy')}</span>
              <Link
                to={`${linkedClient.recordHref}?tab=care#allergies`}
                className="ml-2 text-xs font-semibold underline underline-offset-2 hover:decoration-pepper-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
                data-testid="med-list-allergies-edit-link"
              >
                Change allergies on the record
              </Link>
            </div>
          ) : (
            <input
              id="ml-allergies"
              type="text"
              aria-labelledby="ml-allergies-label"
              className={FIELD_CLS}
              placeholder="[List allergies]"
              value={doc.allergies}
              onChange={(e) => setField({ allergies: e.target.value })}
              data-testid="med-list-allergies"
            />
          )}
        </div>
      </div>

      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-sage-800">Medications</h4>
        <MedListItemsEditor items={items} onChange={(next) => onChange(withItems(doc, next))} careTeam={careTeam} />
      </section>

      <h4 className="text-sm font-semibold text-sage-800">How it will print</h4>
      {total === 0 ? (
        <p className="rounded-[12px] border border-sage-800/[8%] bg-paper p-5 text-sm text-ink-soft" data-testid="med-list-empty">
          {emptyLabel}
        </p>
      ) : (
        <div className="space-y-4" data-testid="med-list-preview">
          {MED_LIST_BUCKETS.map((key) => {
            const rows = doc.buckets[key];
            if (rows.length === 0) return null;
            return (
              <section key={key} data-testid={`med-list-section-${key}`}>
                <h4 className="mb-1.5 text-sm font-semibold uppercase tracking-[0.08em] text-sage-800">
                  {MED_LIST_BUCKET_LABELS[key]}
                </h4>
                <div className="overflow-x-auto rounded-[12px] border border-sage-800/[8%]">
                  <table className="w-full min-w-[640px] text-left text-[13px]">
                    <thead>
                      <tr className="bg-sage-50 text-[12px] font-semibold text-sage-800">
                        {COLUMNS.map((c) => (
                          <th key={c.key} className="px-3 py-2">
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i} className="border-t border-sage-800/[6%]" data-testid="med-list-row">
                          {COLUMNS.map((c) => (
                            <td key={c.key} className="px-3 py-2 align-top text-ink">
                              {c.key === 'medication' ? (
                                <span className="font-semibold">{displayValue(row[c.key], 'medication')}</span>
                              ) : (
                                <span className={row[c.key] ? '' : 'text-ink-soft'}>
                                  {!row[c.key]
                                    ? 'Not given'
                                    : c.key === 'prescriber'
                                      ? row[c.key]
                                      : displayValue(row[c.key], c.key === 'purpose' ? 'medication_reason' : 'medication')}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}

      {changes.length > 0 && (
        <section data-testid="med-list-changes">
          <h4 className="mb-1.5 text-sm font-semibold uppercase tracking-[0.08em] text-sage-800">Changes coming</h4>
          <p className="mb-1.5 text-xs text-ink-soft" data-testid="med-list-changes-source">
            These come from the record. To change one, change the medication on the record.
          </p>
          <div className="overflow-x-auto rounded-[12px] border border-sage-800/[8%]">
            <table className="w-full text-left text-[0.8125rem]">
              <thead>
                <tr className="bg-sage-50 text-[0.75rem] font-semibold text-sage-800">
                  <th className="px-3 py-2">Medication</th>
                  <th className="px-3 py-2">Dose</th>
                  <th className="px-3 py-2">Change</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={i} className="border-t border-sage-800/[6%]" data-testid="med-list-change">
                    <td className="px-3 py-2 align-top font-semibold text-ink">{displayValue(c.medication, 'medication')}</td>
                    <td className={'whitespace-nowrap px-3 py-2 align-top ' + (c.dose ? 'text-ink' : 'text-ink-soft')}>
                      {c.dose ? displayValue(c.dose, 'medication') : 'Not given'}
                    </td>
                    <td className="px-3 py-2 align-top text-ink">{medListChangeWords(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="text-xs text-ink-soft" data-testid="med-list-word-nudge">
        {linkedClient
          ? 'Check every medication. Changes here make the list only; to change the record, change it on the record.'
          : 'Check every medication. The list prints each one in the times ticked.'}
      </p>
    </div>
  );
}
